import React, { useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import { API_URL } from "./config";
import "./styles/SearchWord.css";

const SearchWord = () => {
  const [word, setWord] = useState("");
  const [result, setResult] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [addSuccess, setAddSuccess] = useState(false);
  const [selectedModel, setSelectedModel] = useState("google");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!word) return;

    setLoading(true);
    setError("");
    setResult({});
    setAddSuccess(false);
    try {
      const url = `${API_URL}/search${selectedModel ? `?provider=${encodeURIComponent(selectedModel)}` : ''}`;
      const res = await axios.post(
        url,
        { word },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      // Parse kết quả: server trả về { result: "<JSON string>" }
      let raw = res?.data?.result ?? res?.data;
      let parsedResult;

      if (typeof raw === "string") {
        // Thử parse trực tiếp
        try {
          parsedResult = JSON.parse(raw);
        } catch (_) {
          // Fallback: nếu model bọc trong ```json ... ```
          const match = raw.match(/```json\n([\s\S]*?)\n```/);
          if (match && match[1]) {
            parsedResult = JSON.parse(match[1]);
          } else {
            throw new Error("INVALID_RESULT_FORMAT");
          }
        }
      } else if (typeof raw === "object" && raw !== null) {
        parsedResult = raw;
      } else {
        throw new Error("INVALID_RESULT_FORMAT");
      }

      // Chuẩn hóa dữ liệu
      const processedResult = {
        ...parsedResult,
        word: parsedResult.word || word,
        meaning: parsedResult.meaning || "",
        kanji: parsedResult.kanji || "",
        usage: parsedResult.usage || "",
        example: Array.isArray(parsedResult.example)
          ? parsedResult.example.map(ex => (typeof ex === 'string' ? ex.trim() : '')).join('\n\n')
          : typeof parsedResult.example === 'string' ? parsedResult.example.trim() : "",
        tips: parsedResult.tips || "",
      };

      setResult(processedResult);
      console.log("Parsed API Response:", processedResult);
    } catch (err) {
      console.error("Search error:", err);
      if (err.response && err.response.status === 400) {
        setError("Bad Request: Vui lòng kiểm tra từ bạn nhập.");
      } else if (err.message === "INVALID_RESULT_FORMAT") {
        setError("Định dạng phản hồi không hợp lệ từ server.");
      } else {
        setError("Đã xảy ra lỗi khi tìm kiếm.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddToVocabulary = async () => {
    if (!result || Object.keys(result).length === 0) return;

    setAddSuccess(false);
    setError("");
    try {
      const response = await axios.post(
        `${API_URL}/add`,
        {
          word: result.word,
          meaning: result.meaning,
          kanji: result.kanji,
          usage: result.usage,
          example: result.example,
          tips: result.tips,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      setAddSuccess(true);
      console.log("Add to vocabulary success:", response.data);
    } catch (err) {
      console.error("Add to vocabulary error:", err.response);
      setError("単語帳への追加に失敗しました。");
    }
  };

  // Hàm chuẩn hóa markdown: loại bỏ khoảng trắng đầu dòng, chuẩn hóa dấu *
  function normalizeMarkdown(text) {
    if (!text) return "";
    if (Array.isArray(text)) {
      return text.map(item => item.trim()).join('\n');
    }
    if (typeof text === 'string') {
      return text.trim();
    }
    return text;
  }

  return (
    <div className="search-word-container">
      <div className="search-grid">
        {/* Panel nhập và tìm kiếm */}
        <div className="search-card input-card">
          <div className="search-header">
          </div>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Từ vựng</label>
              <input
                type="text"
                value={word}
                onChange={(e) => setWord(e.target.value)}
                placeholder="Nhập từ cần tìm..."
                required
              />
            </div>
            
            <div className="form-row">
              <select
                value={selectedModel}
                onChange={(e) => setSelectedModel(e.target.value)}
                className="model-select"
              >
                <option value="google">Google</option>
                <option value="openai">OpenAI</option>
              </select>
              <button className="button-search" type="submit" disabled={loading}>
                {loading ? "Đang tìm..." : "Tìm kiếm"}
              </button>
            </div>
          </form>

          {error && <div className="error-box">{error}</div>}
        </div>

        {/* Panel kết quả riêng, có cuộn bên trong */}
        <div className="search-card result-card">
          <div className="result-panel">
            <div className="result-header">Kết quả tìm kiếm</div>
            <div className="result-scroll">
              <div className="result-content">
                {result && Object.keys(result).length > 0 ? (
                  <>
                    <div className="result-item">
                      <strong>Từ vựng:</strong> {result.word}
                    </div>
                    {result.kanji && (
                      <div className="result-item">
                        <strong>漢字:</strong>
                        <div className="result-content">
                          <ReactMarkdown>{normalizeMarkdown(result.kanji)}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                    <div className="result-item">
                      <strong>Ý nghĩa:</strong>
                      <div className="result-content">
                        <ReactMarkdown>{normalizeMarkdown(result.meaning)}</ReactMarkdown>
                      </div>
                    </div>
                    <div className="result-item">
                      <strong>Ví dụ:</strong>
                      <div className="result-content">
                        {normalizeMarkdown(result.example)
                          .replace(/<br\s*\/?>/gi, '\n')
                          .split('\n')
                          .filter(line => line.trim() !== '')
                          .map((line, idx) => (
                            <ReactMarkdown key={idx}>{line}</ReactMarkdown>
                          ))}
                      </div>
                    </div>
                    <div className="result-item">
                      <strong>Cách dùng:</strong>
                      <div className="result-content">
                        <ReactMarkdown>{normalizeMarkdown(result.usage)}</ReactMarkdown>
                      </div>
                    </div>
                    <div className="result-item">
                      <strong>Mẹo học:</strong>
                      <div className="result-content">
                        <ReactMarkdown>{normalizeMarkdown(result.tips)}</ReactMarkdown>
                      </div>
                    </div>
                    
                    <div className="add-section">
                      <button
                        className="add-to-vocabulary"
                        onClick={handleAddToVocabulary}
                        title="Thêm vào danh sách từ vựng"
                        disabled={!result || Object.keys(result).length === 0}
                      >
                        ＋ Thêm vào từ vựng
                      </button>
                      {addSuccess && (
                        <p className="success-message">
                          Đã thêm vào danh sách từ vựng!
                        </p>
                      )}
                      {error && (
                        <p className="error-message">
                          {error}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="placeholder">
                    Kết quả tìm kiếm sẽ hiển thị tại đây.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchWord;
